import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../../stores/auth-store";
import { logout } from "../../lib/auth-service";
import { Icon } from "../ui/Icon";
import { NotificationBell } from "../notifications/NotificationBell";
import { useSidebarNav } from "./useSidebarNav";
import AltArrowRightLinear from "~icons/solar/alt-arrow-right-linear";
import SettingsLinear from "~icons/solar/settings-linear";
import dahlingLogo from "../../assets/dahling_logo_1.svg";
import { cn } from "../../lib/utils";

interface TopBarProps {
  onMobileMenuOpen: () => void;
}

export function TopBar({ onMobileMenuOpen }: TopBarProps) {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { navItems } = useSidebarNav();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const signOutButtonRef = useRef<HTMLButtonElement>(null);

  const firstName = user?.first_name ?? "";
  const lastName = user?.last_name ?? "";
  const displayName =
    firstName || lastName ? `${firstName} ${lastName}`.trim() : "Unknown User";
  const initials =
    firstName || lastName
      ? `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
      : "?";

  // Derive current page from nav items
  const currentNav = navItems.find((item) => {
    const path = typeof item.to === "string" ? item.to : "";
    if (item.end) return location.pathname === path;
    return location.pathname.startsWith(path) && path !== "/";
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    if (isDropdownOpen) {
      requestAnimationFrame(() => signOutButtonRef.current?.focus());
    }
  }, [isDropdownOpen]);

  function handleSignOut() {
    logout();
    navigate("/login", { replace: true });
    queryClient.clear();
  }

  return (
    <header className="relative flex items-center justify-between gap-3 border-b border-border-light bg-white px-4 py-3 lg:px-5 lg:py-5">
      {/* Mobile hamburger button */}
      <button
        type="button"
        className="flex size-11 items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 lg:hidden"
        aria-label="Open navigation menu"
        onClick={onMobileMenuOpen}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>

      {/* Mobile centered logo */}
      <div className="absolute left-1/2 -translate-x-1/2 lg:hidden">
        <img src={dahlingLogo} alt="Dahling" width={107} height={28} />
      </div>

      {/* Breadcrumb — desktop only */}
      <div className="hidden items-center gap-2 lg:flex">
        {currentNav && (
          <>
            <Icon
              icon={currentNav.icon}
              size="md"
              className="text-text-primary"
            />
            <span className="text-body-lg font-medium text-text-primary">
              {currentNav.label}
            </span>
          </>
        )}
      </div>

      {/* Right section */}
      <div className="flex h-11 items-center gap-5">
        {/* Notification bell */}
        <NotificationBell />

        {/* User profile with dropdown */}
        <div ref={dropdownRef} className="relative">
          <button
            type="button"
            className="flex h-11 items-center gap-3"
            aria-label={`User menu for ${displayName}`}
            aria-expanded={isDropdownOpen}
            aria-haspopup="true"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
          >
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-gradient-start to-gradient-end overflow-hidden">
              {user?.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={displayName}
                  className="size-10 object-cover"
                />
              ) : (
                <span className="text-body-md font-semibold text-white">
                  {initials}
                </span>
              )}
            </div>
            <div className="hidden h-10 flex-col justify-center lg:flex">
              <span className="text-body-lg font-medium text-text-primary">
                {displayName}
              </span>
              <span className="text-body-sm font-light text-text-placeholder">
                {user?.email ?? ""}
              </span>
            </div>
            <Icon
              icon={AltArrowRightLinear}
              size="md"
              className={cn(
                "hidden text-text-primary transition-transform duration-200 lg:block",
                isDropdownOpen ? "-rotate-90" : "rotate-90"
              )}
            />
          </button>

          {isDropdownOpen && (
            <div
              className="absolute right-0 top-full z-50 mt-2 min-w-[180px] rounded-lg border border-border-default bg-white py-1 shadow-lg"
              tabIndex={-1}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setIsDropdownOpen(false);
                }
              }}
            >
              <Link
                to="/settings/profile"
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-body-md text-text-primary hover:bg-bg-page focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                onClick={() => setIsDropdownOpen(false)}
              >
                <Icon icon={SettingsLinear} size="sm" className="text-grey" />
                Settings
              </Link>
              <div className="my-1 border-t border-[#f0f0f0]" />
              <button
                ref={signOutButtonRef}
                type="button"
                className="w-full px-4 py-2.5 text-left text-body-md text-error hover:bg-bg-page focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                onClick={handleSignOut}
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
